/**
 * Registry Explorer — DRR-111 Config Type Registry (dev/architect tool).
 *
 * Source of truth for all 15 config types, dimension code tables, and the
 * scope-encoded filename system (DRR-089).
 *
 * Four tabs:
 *   1. Overview      — stats row + pattern info
 *   2. Config Types  — searchable table, expandable rows with dimensions
 *   3. Dimension Codes — dimension table selector + code lookup
 *   4. Filename Decoder — paste a filename, decode its segments live
 *
 * Architecture constraints:
 *   - Mantine v8.x only — no MUI
 *   - Dumb frontend — all data from backend registry queries
 *   - Types from @/generated/graphql
 *   - useAuth() from @/auth/AuthContext
 *   - Repository pattern via Apollo useQuery / useLazyQuery
 *   - No `as any`, no hardcoded values
 */

import { useState } from 'react';
import { useQuery, useLazyQuery } from '@apollo/client/react';
import { useAuth } from '@/auth/AuthContext';
import type {
  RegistryOverview,
  RegistryConfigType,
  RegistryDimensionTable,
  RegistryDimensionDef,
  DecodedFilename,
  RegistryOverviewQuery,
  ConfigTypeRegistryQuery,
  DimensionCodeTablesQuery,
} from '@/generated/graphql';

// ─── GraphQL Documents ────────────────────────────────────────────────────────

// DECODE_CONFIG_FILENAME: canonical query from useDecodeConfigFilename hook
import { DECODE_CONFIG_FILENAME } from '@/hooks/useDecodeConfigFilename';

import {
  useRegistryOverviewQuery,
  useConfigTypeRegistryQuery,
  useDimensionCodeTablesQuery,
  RegistryOverviewDocument,
  ConfigTypeRegistryDocument,
  DimensionCodeTablesDocument,
} from '@/generated/graphql';
import {
  IconDatabase,
  IconSearch,
  IconCode,
  IconTable,
  IconAlertTriangle,
  IconCheck,
  IconX,
  IconChevronDown,
  IconChevronRight,
  IconInfoCircle,
} from '@tabler/icons-react';
import {
  Container,
  Title,
  Text,
  Tabs,
  SimpleGrid,
  Card,
  Group,
  Stack,
  Badge,
  Table,
  TextInput,
  Button,
  Alert,
  Loader,
  Select,
  Collapse,
  Paper,
  ThemeIcon,
  Code,
} from '@mantine/core';
// ─── Query Response Interfaces ────────────────────────────────────────────────

// ─── Example filenames for the decoder tab ────────────────────────────────────

const EXAMPLE_FILENAMES = ['wf-2-0-001', 'tg-4-3-001', 'rs-4-1-004'] as const;

// ─── Overview Tab ─────────────────────────────────────────────────────────────

function OverviewTab() {
  const { data, loading, error } = useQuery<RegistryOverviewQuery>(RegistryOverviewDocument);

  if (loading) {
    return (
      <Group justify="center" py="xl">
        <Loader size="sm" />
        <Text size="sm" c="dimmed">Loading registry overview...</Text>
      </Group>
    );
  }

  if (error) {
    return (
      <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Error">
        {error.message}
      </Alert>
    );
  }

  const overview = data?.registryOverview;

  return (
    <Stack gap="lg">
      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
        Scope-encoded config IDs (DRR-089). Filenames encode WHERE (subgraph, category, sequence);
        the <Code>name:</Code> field encodes WHAT.
        Example: <Code>wf-0-0-001</Code> decodes to core subgraph (0), doc category (0), seq 001.
      </Alert>

      <SimpleGrid cols={{ base: 2, md: 4 }} spacing="md">
        <Card withBorder p="md" radius="md">
          <Stack gap={4} align="center">
            <Text size="xl" fw={700}>{overview?.configTypeCount ?? '—'}</Text>
            <Text size="xs" c="dimmed" ta="center">Config Types</Text>
          </Stack>
        </Card>
        <Card withBorder p="md" radius="md">
          <Stack gap={4} align="center">
            <Text size="xl" fw={700}>{overview?.dimensionTableCount ?? '—'}</Text>
            <Text size="xs" c="dimmed" ta="center">Dimension Tables</Text>
          </Stack>
        </Card>
        <Card withBorder p="md" radius="md">
          <Stack gap={4} align="center">
            <Text size="xl" fw={700}>{overview?.totalDimensionCodes ?? '—'}</Text>
            <Text size="xs" c="dimmed" ta="center">Total Dimension Codes</Text>
          </Stack>
        </Card>
        <Card withBorder p="md" radius="md">
          <Stack gap={4} align="center">
            <Text size="xl" fw={700} ff="monospace" style={{ fontSize: '1rem' }}>
              {overview?.registryVersion ?? '—'}
            </Text>
            <Text size="xs" c="dimmed" ta="center">Registry Version</Text>
          </Stack>
        </Card>
      </SimpleGrid>

      {(overview?.valkeyIndexPattern || overview?.wasabiKeyPattern) && (
        <Paper withBorder p="md" radius="md">
          <Stack gap="sm">
            {overview?.valkeyIndexPattern && (
              <Group gap="xs">
                <Text size="sm" fw={600} w={140}>Valkey Index Pattern:</Text>
                <Text size="sm" ff="monospace" c="violet">{overview.valkeyIndexPattern}</Text>
              </Group>
            )}
            {overview?.wasabiKeyPattern && (
              <Group gap="xs">
                <Text size="sm" fw={600} w={140}>Wasabi Key Pattern:</Text>
                <Text size="sm" ff="monospace" c="teal">{overview.wasabiKeyPattern}</Text>
              </Group>
            )}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}

// ─── Config Type Row (expandable) ─────────────────────────────────────────────

function ConfigTypeRow({ ct }: { ct: RegistryConfigType }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <Table.Tr
        style={{ cursor: 'pointer' }}
        onClick={() => setExpanded((v) => !v)}
      >
        <Table.Td>
          <Group gap={4}>
            {expanded ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
            <Badge variant="outline" size="sm" ff="monospace">{ct.symbol}</Badge>
          </Group>
        </Table.Td>
        <Table.Td>
          <Text size="sm" fw={500}>{ct.name}</Text>
        </Table.Td>
        <Table.Td>
          <Text size="sm" ff="monospace" c="violet">{ct.filenamePrefix}</Text>
        </Table.Td>
        <Table.Td>
          <Text size="xs" ff="monospace" c="blue">{ct.skVerbose}</Text>
        </Table.Td>
        <Table.Td>
          <Text size="xs" ff="monospace" c="teal">{ct.skCompressed}</Text>
        </Table.Td>
        <Table.Td>
          <Text size="xs" c="dimmed">{ct.idField}</Text>
        </Table.Td>
        <Table.Td>
          <Text size="xs" c="dimmed" style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {ct.sourceDirs[0] ?? '—'}
          </Text>
        </Table.Td>
        <Table.Td>
          {ct.legacySk && <Badge size="xs" color="orange" variant="light">legacy</Badge>}
        </Table.Td>
      </Table.Tr>

      {expanded && (
        <Table.Tr>
          <Table.Td colSpan={8} p={0}>
            <Collapse in={expanded}>
              <Paper p="md" m="xs" withBorder radius="sm" bg="var(--mantine-color-gray-0)">
                <Stack gap="sm">
                  {ct.filenameTemplate && (
                    <Group gap="xs">
                      <Text size="xs" fw={600}>Filename template:</Text>
                      <Text size="xs" ff="monospace">{ct.filenameTemplate}</Text>
                    </Group>
                  )}
                  {ct.seedLoader && (
                    <Group gap="xs">
                      <Text size="xs" fw={600}>Seed loader:</Text>
                      <Text size="xs" ff="monospace">{ct.seedLoader}</Text>
                    </Group>
                  )}
                  {ct.dimensions.length > 0 && (
                    <Stack gap={4}>
                      <Text size="xs" fw={600}>Dimensions ({ct.dimensions.length}):</Text>
                      <DimensionMiniTable dimensions={ct.dimensions} />
                    </Stack>
                  )}
                </Stack>
              </Paper>
            </Collapse>
          </Table.Td>
        </Table.Tr>
      )}
    </>
  );
}

function DimensionMiniTable({ dimensions }: { dimensions: RegistryDimensionDef[] }) {
  return (
    <Table withTableBorder withColumnBorders fz="xs" style={{ width: 'auto' }}>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Name</Table.Th>
          <Table.Th>Maps To</Table.Th>
          <Table.Th>Default</Table.Th>
          <Table.Th>Source</Table.Th>
          <Table.Th>Examples</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {dimensions.map((dim) => (
          <Table.Tr key={dim.name}>
            <Table.Td><Text size="xs" fw={500}>{dim.name}</Text></Table.Td>
            <Table.Td><Text size="xs" ff="monospace">{dim.mapsTo ?? '—'}</Text></Table.Td>
            <Table.Td><Text size="xs">{dim.defaultValue ?? '—'}</Text></Table.Td>
            <Table.Td><Text size="xs">{dim.source ?? '—'}</Text></Table.Td>
            <Table.Td>
              <Group gap={4}>
                {dim.examples.map((ex) => (
                  <Badge key={ex} size="xs" variant="dot">{ex}</Badge>
                ))}
              </Group>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

// ─── Config Types Tab ─────────────────────────────────────────────────────────

function ConfigTypesTab() {
  const [search, setSearch] = useState('');
  const { data, loading, error } = useQuery<ConfigTypeRegistryQuery>(ConfigTypeRegistryDocument);

  if (loading) {
    return (
      <Group justify="center" py="xl">
        <Loader size="sm" />
        <Text size="sm" c="dimmed">Loading config types...</Text>
      </Group>
    );
  }

  if (error) {
    return (
      <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Error">
        {error.message}
      </Alert>
    );
  }

  const allTypes = data?.configTypeRegistry ?? [];
  const q = search.toLowerCase();
  const filtered = q
    ? allTypes.filter(
        (ct) =>
          ct.name.toLowerCase().includes(q) ||
          ct.filenamePrefix.toLowerCase().includes(q) ||
          ct.symbol.toLowerCase().includes(q) ||
          ct.idField.toLowerCase().includes(q),
      )
    : allTypes;

  return (
    <Stack gap="sm">
      <TextInput
        leftSection={<IconSearch size={14} />}
        placeholder="Search by name, prefix, symbol, or ID field..."
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        size="sm"
        style={{ maxWidth: 400 }}
      />
      <Text size="xs" c="dimmed">
        {filtered.length} of {allTypes.length} config types. Click a row to expand details.
      </Text>
      <Table.ScrollContainer minWidth={900}>
        <Table withTableBorder highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: 80 }}>Symbol</Table.Th>
              <Table.Th>Name</Table.Th>
              <Table.Th style={{ width: 90 }}>Prefix</Table.Th>
              <Table.Th>SK (verbose)</Table.Th>
              <Table.Th>SK (compressed)</Table.Th>
              <Table.Th>ID Field</Table.Th>
              <Table.Th>Source Dir</Table.Th>
              <Table.Th style={{ width: 60 }}></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filtered.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={8}>
                  <Text size="sm" c="dimmed" ta="center" py="md">No config types match the search.</Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              filtered.map((ct) => <ConfigTypeRow key={ct.name} ct={ct} />)
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Stack>
  );
}

// ─── Dimension Codes Tab ──────────────────────────────────────────────────────

function DimensionCodesTab() {
  const [selectedTable, setSelectedTable] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const { data, loading, error } = useQuery<DimensionCodeTablesQuery>(DimensionCodeTablesDocument);

  if (loading) {
    return (
      <Group justify="center" py="xl">
        <Loader size="sm" />
        <Text size="sm" c="dimmed">Loading dimension code tables...</Text>
      </Group>
    );
  }

  if (error) {
    return (
      <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Error">
        {error.message}
      </Alert>
    );
  }

  const tables = data?.dimensionCodeTables ?? [];
  const activeTable = tables.find((t) => t.name === selectedTable) ?? tables[0] ?? null;

  const q = search.toLowerCase();
  const filteredCodes = activeTable
    ? activeTable.codes.filter(
        (c) => !q || c.value.toLowerCase().includes(q) || c.code.includes(q),
      )
    : [];

  return (
    <Stack gap="sm">
      <Group gap="sm">
        <Select
          label="Dimension table"
          placeholder="Pick a table..."
          data={tables.map((t) => ({
            value: t.name,
            label: `${t.name} (${t.count} codes)`,
          }))}
          value={selectedTable ?? (tables[0]?.name ?? null)}
          onChange={(v) => {
            setSelectedTable(v);
            setSearch('');
          }}
          style={{ minWidth: 280 }}
          size="sm"
        />
        <TextInput
          label="Filter codes"
          leftSection={<IconSearch size={14} />}
          placeholder="value or code..."
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          size="sm"
          style={{ minWidth: 200 }}
        />
      </Group>

      {activeTable && (
        <>
          <Text size="xs" c="dimmed">
            Showing {filteredCodes.length} of {activeTable.count} codes in <strong>{activeTable.name}</strong>
          </Text>
          <Table.ScrollContainer minWidth={300}>
            <Table withTableBorder withColumnBorders highlightOnHover style={{ maxWidth: 480 }}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Value</Table.Th>
                  <Table.Th style={{ width: 100 }}>Numeric Code</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filteredCodes.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={2}>
                      <Text size="sm" c="dimmed" ta="center" py="sm">No codes match.</Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  filteredCodes.map((c) => (
                    <Table.Tr key={c.code}>
                      <Table.Td><Text size="sm">{c.value}</Text></Table.Td>
                      <Table.Td>
                        <Text size="sm" fw={700} ff="monospace">{c.code}</Text>
                      </Table.Td>
                    </Table.Tr>
                  ))
                )}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </>
      )}
    </Stack>
  );
}

// ─── Filename Decoder Tab ─────────────────────────────────────────────────────

function FilenameDecoderTab() {
  const [input, setInput] = useState('');

  const [decodeFilename, { data, loading, error, called }] = useLazyQuery<
    { decodeConfigFilename: DecodedFilename }
  >(DECODE_CONFIG_FILENAME);

  const handleDecode = () => {
    const filename = input.trim();
    if (!filename) return;
    void decodeFilename({ variables: { filename } });
  };

  const result = data?.decodeConfigFilename;

  return (
    <Stack gap="md" style={{ maxWidth: 640 }}>
      <Text size="sm" c="dimmed">
        Paste a scope-encoded config filename (e.g., <Code>wf-2-0-001</Code>) to decode its
        subgraph, category, and sequence dimensions (DRR-089).
      </Text>

      <Group gap="xs" mb={4}>
        <Text size="xs" c="dimmed">Examples:</Text>
        {EXAMPLE_FILENAMES.map((ex) => (
          <Badge
            key={ex}
            size="sm"
            variant="light"
            color="blue"
            ff="monospace"
            style={{ cursor: 'pointer' }}
            onClick={() => setInput(ex)}
          >
            {ex}
          </Badge>
        ))}
      </Group>

      <Group gap="sm" align="flex-end">
        <TextInput
          label="Config filename"
          placeholder="e.g. wf-2-0-001"
          value={input}
          onChange={(e) => setInput(e.currentTarget.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleDecode()}
          size="sm"
          style={{ flex: 1 }}
          ff="monospace"
        />
        <Button
          size="sm"
          leftSection={loading ? <Loader size={12} color="white" /> : <IconCode size={14} />}
          onClick={handleDecode}
          disabled={!input.trim() || loading}
        >
          Decode
        </Button>
      </Group>

      {error && (
        <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Query Error">
          {error.message}
        </Alert>
      )}

      {called && !loading && result && (
        <>
          {result.valid ? (
            <Alert
              icon={<IconCheck size={16} />}
              color="green"
              title={`${result.configType ?? 'Unknown'} (prefix: ${result.prefix ?? '—'}, symbol: ${result.symbol ?? '—'})`}
              variant="light"
            >
              Filename decoded successfully.
            </Alert>
          ) : (
            <Alert icon={<IconX size={16} />} color="red" title="Invalid Filename" variant="light">
              {result.error ?? 'Could not decode this filename.'}
            </Alert>
          )}

          {result.segments.length > 0 && (
            <Table withTableBorder withColumnBorders style={{ maxWidth: 480 }}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Dimension</Table.Th>
                  <Table.Th>Raw Code</Table.Th>
                  <Table.Th>Decoded Value</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {result.segments.map((seg: { dimension: string; rawCode: string; decodedValue?: string | null }) => (
                  <Table.Tr key={seg.dimension}>
                    <Table.Td><Text size="sm">{seg.dimension}</Text></Table.Td>
                    <Table.Td>
                      <Text size="sm" ff="monospace">{seg.rawCode}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={700}>{seg.decodedValue ?? '—'}</Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </>
      )}
    </Stack>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function RegistryExplorerPage() {
  // Auth is available for scope-gating if needed (architect layer)
  useAuth();

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        <Group gap="sm">
          <ThemeIcon size="lg" variant="light" color="violet">
            <IconDatabase size={20} />
          </ThemeIcon>
          <div>
            <Title order={2}>Registry Explorer</Title>
            <Text size="sm" c="dimmed">
              DRR-111 — Config type registry, dimension codes, filename decoder
            </Text>
          </div>
        </Group>

        <Tabs defaultValue="overview" keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value="overview" leftSection={<IconInfoCircle size={14} />}>
              Overview
            </Tabs.Tab>
            <Tabs.Tab value="config-types" leftSection={<IconTable size={14} />}>
              Config Types
            </Tabs.Tab>
            <Tabs.Tab value="dimension-codes" leftSection={<IconDatabase size={14} />}>
              Dimension Codes
            </Tabs.Tab>
            <Tabs.Tab value="decoder" leftSection={<IconCode size={14} />}>
              Filename Decoder
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="overview" pt="md">
            <OverviewTab />
          </Tabs.Panel>

          <Tabs.Panel value="config-types" pt="md">
            <ConfigTypesTab />
          </Tabs.Panel>

          <Tabs.Panel value="dimension-codes" pt="md">
            <DimensionCodesTab />
          </Tabs.Panel>

          <Tabs.Panel value="decoder" pt="md">
            <FilenameDecoderTab />
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
}

export default RegistryExplorerPage;
